import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminInviteWaitlistEntry,
  adminWaitlistCsvExportUrl,
  adminWaitlistMarkConverted,
  adminWaitlistSoftDelete,
  getAdminWaitlist,
  getAdminWaitlistMetrics,
  type AdminWaitlistEntry,
} from "@/api/admin";
import { Card, SectionLabel } from "@/components/ui/Card";

type FilterState = "all" | "founding" | "standard" | "converted" | "removed";
type SortState = "joined_desc" | "joined_asc" | "position_asc" | "position_desc";

const LANG_FLAG: Record<string, string> = {
  en: "🇬🇧",
  ru: "🇷🇺",
  uk: "🇺🇦",
  es: "🇪🇸",
  de: "🇩🇪",
};

const PAGE_SIZE = 50;

export default function AdminWaitlist() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<FilterState>("all");
  const [sort, setSort] = useState<SortState>("joined_desc");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const metrics = useQuery({
    queryKey: ["admin-waitlist-metrics"],
    queryFn: getAdminWaitlistMetrics,
    refetchInterval: 30_000,
  });

  const params = (() => {
    const p: Parameters<typeof getAdminWaitlist>[0] = {
      sort,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (search.trim()) p.q = search.trim();
    if (filter === "founding") p.founding = true;
    else if (filter === "standard") p.founding = false;
    else if (filter === "converted") p.converted = true;
    else if (filter === "removed") p.include_removed = true;
    return p;
  })();

  const list = useQuery({
    queryKey: ["admin-waitlist-list", params],
    queryFn: () => getAdminWaitlist(params),
  });

  const invite = useMutation({
    mutationFn: adminInviteWaitlistEntry,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-waitlist-list"] }),
  });
  const convert = useMutation({
    mutationFn: (id: number) => adminWaitlistMarkConverted(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-waitlist-list"] });
      qc.invalidateQueries({ queryKey: ["admin-waitlist-metrics"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => adminWaitlistSoftDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-waitlist-list"] });
      qc.invalidateQueries({ queryKey: ["admin-waitlist-metrics"] });
    },
  });

  const m = metrics.data;
  const foundingProgressPct =
    m && m.founding_total > 0 ? (m.founding_claimed / m.founding_total) * 100 : 0;

  return (
    <div className="space-y-5">
      {/* Metrics tiles */}
      <Card>
        <div className="flex flex-wrap gap-6">
          <Tile label="Total" primary={String(m?.total ?? "—")} />
          <Tile
            label={`Founding ${m?.founding_claimed ?? 0}/${m?.founding_total ?? 500}`}
            primary={`${m?.founding_remaining ?? "—"} left`}
          >
            <div className="mt-2 h-1.5 w-32 overflow-hidden rounded-full bg-cream-border">
              <div
                className="h-full bg-amber"
                style={{ width: `${Math.min(100, foundingProgressPct)}%` }}
              />
            </div>
          </Tile>
          <Tile label="Last 24h" primary={String(m?.last_24h ?? "—")} />
          <Tile label="Last 7d" primary={String(m?.last_7d ?? "—")} />
          <Tile label="Converted" primary={String(m?.converted ?? "—")} />
          <Tile label="Removed" primary={String(m?.removed ?? "—")} />
          <Tile
            label="Languages"
            primary={
              m
                ? Object.entries(m.by_lang)
                    .map(([l, n]) => `${(LANG_FLAG[l] || l).toUpperCase()} ${n}`)
                    .join(" · ") || "—"
                : "—"
            }
          />
        </div>
      </Card>

      {/* Filters */}
      <Card className="flex flex-wrap items-end gap-3">
        <div>
          <SectionLabel>Search</SectionLabel>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="email contains…"
            className="mt-1 rounded-md border border-cream-border bg-cream-light px-3 py-2 text-[13px]"
          />
        </div>
        <div>
          <SectionLabel>Filter</SectionLabel>
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value as FilterState);
              setPage(0);
            }}
            className="mt-1 rounded-md border border-cream-border bg-cream-light px-3 py-2 text-[13px]"
          >
            <option value="all">All active</option>
            <option value="founding">Founding (#1-500)</option>
            <option value="standard">Standard (#501+)</option>
            <option value="converted">Converted</option>
            <option value="removed">Include removed</option>
          </select>
        </div>
        <div>
          <SectionLabel>Sort</SectionLabel>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortState)}
            className="mt-1 rounded-md border border-cream-border bg-cream-light px-3 py-2 text-[13px]"
          >
            <option value="joined_desc">Newest first</option>
            <option value="joined_asc">Oldest first</option>
            <option value="position_asc">Position #1 → N</option>
            <option value="position_desc">Position N → #1</option>
          </select>
        </div>
        <div className="ml-auto">
          <a
            href={adminWaitlistCsvExportUrl(filter === "removed")}
            download
            className="inline-block rounded-md border border-amber bg-amber/15 px-3 py-2 text-[13px] text-amber-dark hover:bg-amber/25"
          >
            Download CSV
          </a>
        </div>
      </Card>

      {/* Table */}
      <Card>
        {list.isLoading ? (
          <div className="py-6 text-center text-taupe">Loading…</div>
        ) : !list.data ? (
          <div className="py-6 text-center text-coral">Failed to load.</div>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between text-[12px] text-taupe">
              <span>
                {list.data.filtered_total} matches · page {page + 1} of{" "}
                {Math.max(1, Math.ceil(list.data.filtered_total / PAGE_SIZE))}
              </span>
              <span className="flex gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                  className="rounded border border-cream-border px-2 py-1 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  disabled={(page + 1) * PAGE_SIZE >= list.data.filtered_total}
                  onClick={() => setPage(page + 1)}
                  className="rounded border border-cream-border px-2 py-1 disabled:opacity-40"
                >
                  Next
                </button>
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-cream-border text-left text-taupe">
                    <th className="py-2 pr-3 font-medium">#</th>
                    <th className="py-2 pr-3 font-medium">Email</th>
                    <th className="py-2 pr-3 font-medium">Lang</th>
                    <th className="py-2 pr-3 font-medium">Joined</th>
                    <th className="py-2 pr-3 font-medium">Founding</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.entries.map((e) => (
                    <Row
                      key={e.id}
                      e={e}
                      onInvite={() => invite.mutate(e.id)}
                      onConvert={() => convert.mutate(e.id)}
                      onRemove={() => {
                        if (confirm(`Remove ${e.email}?`)) remove.mutate(e.id);
                      }}
                    />
                  ))}
                  {list.data.entries.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-4 text-center text-taupe">
                        No entries match.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function Tile({
  label,
  primary,
  children,
}: {
  label: string;
  primary: string;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1 font-serif text-[20px] font-medium text-navy-deep tabular-nums">
        {primary}
      </div>
      {children}
    </div>
  );
}

function Row({
  e,
  onInvite,
  onConvert,
  onRemove,
}: {
  e: AdminWaitlistEntry;
  onInvite: () => void;
  onConvert: () => void;
  onRemove: () => void;
}) {
  const status = e.removed_at
    ? "removed"
    : e.converted_user_id != null
      ? "converted"
      : e.invited
        ? "invited"
        : "pending";
  const statusClass = {
    removed: "bg-cream-border text-taupe",
    converted: "bg-amber/30 text-amber-dark",
    invited: "bg-cream-light text-navy-deep border border-cream-border",
    pending: "bg-cream-light text-taupe border border-cream-border",
  }[status];
  return (
    <tr className="border-b border-cream-border/60 align-middle">
      <td className="py-2 pr-3 tabular-nums text-taupe">{e.position}</td>
      <td className="py-2 pr-3 break-all">
        <a href={`mailto:${e.email}`} className="text-amber-dark hover:underline">
          {e.email}
        </a>
      </td>
      <td className="py-2 pr-3">
        {e.lang ? `${LANG_FLAG[e.lang] || ""} ${e.lang.toUpperCase()}` : "—"}
      </td>
      <td className="py-2 pr-3 text-taupe" title={e.created_at ?? ""}>
        {e.created_at ? relativeTime(e.created_at) : "—"}
      </td>
      <td className="py-2 pr-3">{e.is_founding ? "✓" : "—"}</td>
      <td className="py-2 pr-3">
        <span className={`rounded-full px-2 py-0.5 text-[11px] ${statusClass}`}>{status}</span>
      </td>
      <td className="py-2 space-x-1 whitespace-nowrap">
        {!e.invited && !e.removed_at ? (
          <button
            onClick={onInvite}
            className="rounded border border-cream-border bg-cream-light px-2 py-1 text-[11px] hover:bg-cream"
          >
            Invite
          </button>
        ) : null}
        {e.converted_user_id == null && !e.removed_at ? (
          <button
            onClick={onConvert}
            className="rounded border border-amber bg-amber/15 px-2 py-1 text-[11px] text-amber-dark hover:bg-amber/25"
          >
            Convert
          </button>
        ) : null}
        {!e.removed_at ? (
          <button
            onClick={onRemove}
            className="rounded border border-coral/40 bg-coral/10 px-2 py-1 text-[11px] text-coral hover:bg-coral/20"
          >
            Remove
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}
